// @erp/module-auth/fe/next — the `?next=` return path, guarded.
//
// The reading gate (ADR-010 §2.12) sends a reader from /read/<slug> to /login,
// /register or /verify-email with `?next=/read/<slug>` so that, once the door is
// behind them, they land back on the document they came for. A return path
// taken off the URL is the classic open redirect: `?next=https://evil.example`
// or `?next=//evil.example` on a sign-in page is a phishing link that carries
// the real site's address. So the value is accepted ONLY as a same-origin PATH
// — one leading slash, then anything but a second slash or a backslash — and
// everything else heals to "no return path" (the screens then go to "/").
//
// TanStack's `navigate({ href })` is what consumes the result: a relative href
// is parsed into path + search (so `/read/x?locale=hi` keeps its query), while
// an ABSOLUTE URL would trigger a full-document navigation off-site — exactly
// what the guard exists to stop, and why the guard runs at the READ as well as
// in the route's validateSearch: /login has no validateSearch (it is a portal
// route, not a module route), and a belt is cheap.
import { useRouterState } from "@tanstack/solid-router";

/**
 * A same-origin return path, or undefined. Accepts `/read/x`, `/read/x?locale=hi`,
 * `/account`; rejects `//evil`, `/\evil`, `https://evil`, `javascript:…`, empty
 * and non-string values, and anything carrying a control character.
 */
export function safeNextPath(value: unknown): string | undefined {
  if (typeof value !== "string") return undefined;
  if (value.length === 0 || value.length > 2048) return undefined;
  // One slash, then not a slash or a backslash: `//host` and `/\host` are the
  // two protocol-relative spellings browsers honour.
  if (!/^\/(?![/\\])/.test(value)) return undefined;
  // No backslash anywhere (some parsers treat it as a slash) and no control
  // characters (a CR/LF here is a header-injection shape on the way back out).
  for (let i = 0; i < value.length; i++) {
    const code = value.charCodeAt(i);
    if (code < 0x20 || code === 0x7f || code === 0x5c) return undefined;
  }
  return value;
}

/** The search shape the guarded routes declare — `validateSearch` on /register and /verify-email. */
export interface NextSearch {
  next?: string;
}

/**
 * The route-level guard: heals a bad `?next=` to absent rather than throwing
 * (the self-healing validateSearch contract). The key is RETURNED EXPLICITLY,
 * as `undefined` when refused: TanStack merges a match's search over its
 * parents' — the root has no validator and passes the raw URL through — so a
 * validator that merely omitted the key would leave the raw value standing on
 * `Route.useSearch()`. A present `undefined` overrides it. The screens still
 * re-apply the guard at the read (useNextPath): a belt, and the only guard
 * /login has.
 */
export function validateNextSearch(input: Record<string, unknown>): NextSearch {
  return { next: safeNextPath(input.next) };
}

/**
 * The current URL's `?next=`, guarded — an accessor a screen reads when it is
 * time to leave. Read structurally off the router state rather than through
 * `Route.useSearch()` (which widens to `any` in a portable module, and which
 * /login — a portal route — cannot offer at all).
 */
export function useNextPath(): () => string | undefined {
  const search = useRouterState({ select: (s) => (s.location.search as { next?: unknown }).next });
  return () => safeNextPath(search());
}

/**
 * Where a screen goes once its job is done: the guarded `next`, or the portal's
 * home. The one place the "/" fallback is spelled.
 */
export const nextOrHome = (next: string | undefined): string => next ?? "/";
